/**
 * excelExport — thin wrapper over SheetJS (xlsx) for the VAT reports.
 *
 * Builds workbooks from an array-of-arrays so callers keep full control over
 * title/summary rows above the table. Numeric cells are kept as real numbers
 * (not formatted strings) so Excel/accountants can sum them.
 */

import * as XLSX from 'xlsx';

/** Build one worksheet from a sheet spec (shared by the single- and multi-sheet exports). */
function buildSheet({ columns, rows = [], preRows = [], postRows = [] }) {
  const header = columns.map(c => c.header);
  const body = rows.map(r =>
    columns.map(c => (typeof c.value === 'function' ? c.value(r) : r[c.key]))
  );

  const aoa = [...preRows, header, ...body, ...postRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = columns.map(c => ({ wch: c.width || 16 }));

  // Apply a number format to numeric columns across the data rows.
  const headerRowIdx = preRows.length; // 0-based index of the header row
  columns.forEach((c, ci) => {
    if (!c.numFmt) return;
    for (let i = 0; i < body.length; i++) {
      const ref = XLSX.utils.encode_cell({ r: headerRowIdx + 1 + i, c: ci });
      const cell = ws[ref];
      if (cell && typeof cell.v === 'number') cell.z = c.numFmt;
    }
  });

  return ws;
}

/**
 * @param {object} opts
 * @param {string}   opts.filename   e.g. 'vat-sales-2026-05.xlsx'
 * @param {string}   opts.sheetName
 * @param {Array}    opts.columns    [{ header, key, value?(row), width?, numFmt? }]
 * @param {Array}    opts.rows       array of row objects
 * @param {Array}    opts.preRows    AOA rows rendered above the header (title/summary)
 * @param {Array}    opts.postRows   AOA rows rendered below the data (totals)
 */
export function exportRowsToXlsx({ filename, sheetName = 'Sheet1', ...sheet }) {
  exportSheetsToXlsx({ filename, sheets: [{ sheetName, ...sheet }] });
}

/**
 * One workbook, several sheets — used for the monthly tax hand-off to the
 * bookkeeper (cover + output VAT + input VAT in a single file).
 *
 * @param {object} opts
 * @param {string} opts.filename
 * @param {Array}  opts.sheets  [{ sheetName, columns, rows, preRows, postRows }]
 */
export function exportSheetsToXlsx({ filename, sheets = [] }) {
  const wb = XLSX.utils.book_new();

  sheets.forEach((sheet, index) => {
    // ชื่อชีตใน Excel ยาวได้ 31 ตัวอักษร และห้ามซ้ำ — ตัดกันพังตั้งแต่ต้น
    const name = String(sheet.sheetName || `Sheet${index + 1}`).slice(0, 31);
    XLSX.utils.book_append_sheet(wb, buildSheet(sheet), name);
  });

  XLSX.writeFile(wb, filename);
}

export const MONEY_FMT = '#,##0.00';
